import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Youtube, Linkedin } from 'lucide-react';
import { toast } from 'sonner';
import { ASSETS, CONTACTS, SOCIALS, POLICIES_PDF } from '../lib/products';
import api from '../lib/api';
import KrispButton from '../components/KrispButton';

const COLS = [
  ['SHOP', [['MASALA KRUNCH', '/product/masala-krunch'], ['CHEESE & HERBS', '/product/cheese-herbs'], ['VOTE FLAVOUR #3', '/vote'], ['GIFT BUNDLES', '/gifting']]],
  ['BRAND', [['ABOUT US', '/about'], ['THE TEAM', '/about#team'], ['SOCIALS', '/socials'], ['CONTACT', '/about#contact']]],
  ['ACCOUNT', [['MY ACCOUNT', '/account'], ['PAYMENTS', '/payments'], ['POLICIES', POLICIES_PDF], ['SHIPPING & RETURNS', POLICIES_PDF], ['PRIVACY', POLICIES_PDF]]],
];
const isExternal = (to) => to.startsWith('http') || to.endsWith('.pdf');

export default function Footer() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const join = async (e) => {
    e.preventDefault();
    if (!email.includes('@')) return;
    setBusy(true);
    try {
      await api.post('/newsletter', { email });
      toast("YOU'RE IN. WELCOME TO THE CRUNCH CLUB ✓");
      setEmail('');
    } catch {
      toast('THAT EMAIL FUMBLED. TRY AGAIN.');
    } finally { setBusy(false); }
  };

  return (
    <footer data-testid="footer" className="relative bg-cocoa text-cream pt-0 pb-8 overflow-hidden rounded-t-[2.5rem]">
      <div data-testid="footer-contact-strip" className="bg-ink border-b-4 border-kred">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-4 flex flex-wrap justify-center gap-x-8 gap-y-2">
          {CONTACTS.map((c) => (
            <a key={c.label} href={c.href} className="flex flex-col leading-tight hover:text-kyellow transition-colors">
              <span className="font-cond text-[8px] tracking-[0.3em] text-kyellow">{c.label}</span>
              <span className="text-xs font-bold">{c.value}</span>
            </a>
          ))}
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-14">
        <div className="flex flex-col items-center text-center">
          <img src={ASSETS.logo} alt="PROKRISP" className="h-16 object-contain" data-testid="footer-logo" />
          <p className="font-display text-[16vw] sm:text-[9vw] leading-none mt-6 select-none text-kyellow">
            GO <span className="stroke-cream">BEYOND.</span>
          </p>
          <p className="font-cond text-sm tracking-[0.3em] text-cream/60 mt-2">15G PROTEIN · ROASTED & PLANT BASED · NO PALM OIL</p>
        </div>

        <div className="grid md:grid-cols-[1.4fr_1fr_1fr_1fr] gap-10 mt-14">
          <div>
            <p className="font-cond font-bold text-2xl">JOIN THE <span className="text-kyellow">CRUNCH CLUB.</span></p>
            <p className="text-xs text-cream/60 mt-2">Drops, flavours, secret codes. First.</p>
            <form onSubmit={join} className="flex flex-col sm:flex-row gap-3 mt-5">
              <input data-testid="newsletter-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email" className="k-input flex-1" />
              <KrispButton testid="newsletter-submit" type="submit" disabled={busy} className="!px-6">LET ME IN →</KrispButton>
            </form>
            <div className="flex gap-3 mt-6">
              {SOCIALS.map((so) => {
                const Icon = so.id === 'instagram' ? Instagram : so.id === 'youtube' ? Youtube : Linkedin;
                return (
                  <a key={so.id} data-testid={`social-${so.id}`} aria-label={so.name} href={so.href || '/socials'} target={so.href ? '_blank' : undefined} rel="noreferrer"
                    className="w-11 h-11 rounded-full border-2 border-cream/30 flex items-center justify-center text-cream/70 hover:bg-kyellow hover:text-cocoa hover:border-kyellow transition-colors duration-200">
                    <Icon size={17} />
                  </a>
                );
              })}
            </div>
          </div>
          {COLS.map(([title, links]) => (
            <div key={title}>
              <p className="font-cond font-bold text-kyellow tracking-[0.2em] mb-4">{title}</p>
              <ul className="space-y-2.5">
                {links.map(([l, to]) => (
                  <li key={l}>
                    {isExternal(to) ? (
                      <a href={to} target="_blank" rel="noreferrer" data-testid={`footer-link-${l.toLowerCase().replace(/\s|&/g, '-')}`} data-cursor="plus"
                        className="text-sm text-cream/70 hover:text-cream transition-colors duration-200">{l}</a>
                    ) : (
                    <Link to={to} data-testid={`footer-link-${l.toLowerCase().replace(/\s/g, '-')}`} data-cursor="plus"
                      className="text-sm text-cream/70 hover:text-cream transition-colors duration-200">{l}</Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-cream/15 mt-14 pt-6 flex flex-col sm:flex-row justify-between gap-3 text-[10px] tracking-[0.2em] text-cream/40">
          <p>© 2026 PROKRISP FOODS PVT. LTD. · GO BEYOND™</p>
          <p>FSSAI LIC. NO. 10018064001526 · HELLO@PROKRISP.COM</p>
        </div>
      </div>
    </footer>
  );
}
